import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AdminSection } from "./admin";
import { AdminButton, AdminDialog, Field, Input, Row, TableShell, Textarea } from "@/components/admin/AdminUI";
import { MediaUpload } from "@/components/admin/MediaUpload";
import { SlugPicker } from "@/components/admin/SlugPicker";
import { getRelationOptions, type RelationOptions } from "@/lib/relation-options.functions";
import { generateActivationDraft, generateActivationArt } from "@/lib/activations.functions";

import {
  ACTIVATION_SERIES,
  ACTIVATION_SLOTS,
  SERIES_BY_SLUG,
  SLOT_BY_SLUG,
  publishAtForSlot,
  type ActivationSeries,
  type ActivationSlot,
  type ActivationStatus,
} from "@/lib/activation-series";

export const Route = createFileRoute("/_authenticated/admin/activations")({
  component: ActivationsAdmin,
});

type A = Database["public"]["Tables"]["activations"]["Row"];
type Ins = Database["public"]["Tables"]["activations"]["Insert"];

type StatusFilter = "all" | ActivationStatus;

const EMPTY: Partial<A> = {
  slug: "",
  title: "",
  subtitle: "",
  category: "anytime",
  slot: "anytime",
  series: "resonance-calibration",
  status: "draft",
  publish_at: null,
  time_label: "",
  duration: "",
  breath_pattern: "box",
  intro: "",
  instruction: "",
  intention: "",
  transmission: "",
  scroll_card: "",
  caption: "",
  phrase: "",
  journal: "",
  reflection_question: "",
  hero_image_url: "",
  wallpaper_url: "",
  audio_url: "",
  video_url: "",
  sort_order: 0,
};

function ActivationsAdmin() {
  const [rows, setRows] = useState<A[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<A | "new" | null>(null);
  const [filter, setFilter] = useState<StatusFilter>("all");

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("activations")
      .select("*")
      .order("publish_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });
    setRows((data ?? []) as A[]);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => filter === "all" ? rows : rows.filter((r) => r.status === filter),
    [rows, filter],
  );

  async function remove(id: string) {
    if (!confirm("Delete this activation? This cannot be undone.")) return;
    const { error } = await supabase.from("activations").delete().eq("id", id);
    if (error) alert(error.message); else load();
  }

  async function duplicate(r: A) {
    const stamp = new Date().toISOString().replace(/[^0-9]/g, "").slice(0, 12);
    const copy: Ins = {
      ...r,
      id: undefined,
      slug: `${r.slug}-copy-${stamp.slice(-4)}`,
      title: `${r.title} (copy)`,
      status: "draft",
      publish_at: null,
      activation_number: null,
      share_count: 0,
      view_count: 0,
      created_at: undefined,
      updated_at: undefined,
    };
    const { error } = await supabase.from("activations").insert(copy);
    if (error) alert(error.message); else load();
  }

  const statusCounts = useMemo(() => ({
    all: rows.length,
    draft: rows.filter((r) => r.status === "draft").length,
    scheduled: rows.filter((r) => r.status === "scheduled").length,
    published: rows.filter((r) => r.status === "published").length,
  }), [rows]);

  return (
    <AdminSection
      title="Activations"
      description="Daily transmissions across the 10-series canon. Schedule ahead — cron auto-publishes at the exact hour."
      action={<AdminButton onClick={() => setEditing("new")}>+ New activation</AdminButton>}
    >
      <div className="mb-6 flex flex-wrap gap-2">
        {(["all", "draft", "scheduled", "published"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] transition ${
              filter === s ? "border-gold bg-gold text-obsidian" : "hairline text-muted-foreground hover:border-gold hover:text-gold"
            }`}
          >
            {s} · {statusCounts[s]}
          </button>
        ))}
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
       filtered.length === 0 ? <p className="text-sm text-muted-foreground">None in this view.</p> :
        <TableShell
          headers={["#", "Title", "Series", "Slot", "Status", "Publish", ""]}
          rows={filtered.map((r) => (
            <tr key={r.id} className="hover:bg-obsidian/40">
              <td className="px-4 py-3 text-muted-foreground">{r.activation_number ?? "—"}</td>
              <td className="px-4 py-3">
                <div className="font-serif text-base text-gold-luminous">{r.title}</div>
                <div className="text-xs text-muted-foreground">/{r.slug}</div>
              </td>
              <td className="px-4 py-3 text-muted-foreground text-xs">
                {r.series ? SERIES_BY_SLUG[r.series as ActivationSeries]?.label ?? r.series : "—"}
              </td>
              <td className="px-4 py-3 text-muted-foreground text-xs">
                {r.slot ? SLOT_BY_SLUG[r.slot as ActivationSlot]?.label ?? r.slot : "—"}
              </td>
              <td className="px-4 py-3">
                <StatusPill status={r.status as ActivationStatus} />
              </td>
              <td className="px-4 py-3 text-muted-foreground text-xs">
                {r.publish_at ? new Date(r.publish_at).toLocaleString() : "—"}
              </td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  <AdminButton variant="ghost" onClick={() => setEditing(r)}>Edit</AdminButton>
                  <AdminButton variant="ghost" onClick={() => duplicate(r)}>Duplicate</AdminButton>
                  <AdminButton variant="danger" onClick={() => remove(r.id)}>Delete</AdminButton>
                </div>
              </td>
            </tr>
          ))}
        />
      }
      {editing && (
        <Form
          initial={editing === "new" ? EMPTY : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </AdminSection>
  );
}

function StatusPill({ status }: { status: ActivationStatus }) {
  const map: Record<ActivationStatus, string> = {
    draft: "border-muted-foreground/30 text-muted-foreground",
    scheduled: "border-gold/40 text-gold",
    published: "border-emerald-500/40 text-emerald-400",
  };
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] ${map[status]}`}>
      {status}
    </span>
  );
}

function Form({ initial, onClose, onSaved }: { initial: Partial<A>; onClose: () => void; onSaved: () => void }) {
  const isNew = !initial.id;

  const initialPublishDate = initial.publish_at
    ? new Date(initial.publish_at).toISOString().slice(0, 10)
    : new Date().toISOString().slice(0, 10);

  const [f, setF] = useState({
    slug: initial.slug ?? "",
    title: initial.title ?? "",
    subtitle: initial.subtitle ?? "",
    series: (initial.series ?? "resonance-calibration") as ActivationSeries,
    slot: (initial.slot ?? "anytime") as ActivationSlot,
    status: (initial.status ?? "draft") as ActivationStatus,
    publish_date: initialPublishDate,
    duration: initial.duration ?? "",
    breath_pattern: initial.breath_pattern ?? "box",
    intro: initial.intro ?? "",
    intention: initial.intention ?? "",
    instruction: initial.instruction ?? "",
    transmission: initial.transmission ?? "",
    scroll_card: initial.scroll_card ?? "",
    caption: initial.caption ?? "",
    phrase: initial.phrase ?? "",
    journal: initial.journal ?? "",
    reflection_question: initial.reflection_question ?? "",
    hero_image_url: initial.hero_image_url ?? "",
    wallpaper_url: initial.wallpaper_url ?? "",
    audio_url: initial.audio_url ?? "",
    video_url: initial.video_url ?? "",
    // Unified publishing fields
    theme: (initial as { theme?: string }).theme ?? "",
    difficulty: (initial as { difficulty?: string }).difficulty ?? "",
    estimated_minutes: ((initial as { estimated_minutes?: number }).estimated_minutes ?? "").toString(),
    tags: ((initial as { tags?: string[] }).tags ?? []).join(", "),
    journal_prompt: (initial as { journal_prompt?: string }).journal_prompt ?? "",
    meditation: (initial as { meditation?: string }).meditation ?? "",
    narration_url: (initial as { narration_url?: string }).narration_url ?? "",
    related_gate_n: ((initial as { related_gate_n?: number }).related_gate_n ?? "").toString(),
    related_music_slug: (initial as { related_music_slug?: string }).related_music_slug ?? "",
    related_book_slug: (initial as { related_book_slug?: string }).related_book_slug ?? "",
    related_wisdom_slug: (initial as { related_wisdom_slug?: string }).related_wisdom_slug ?? "",
    related_challenge_slug: (initial as { related_challenge_slug?: string }).related_challenge_slug ?? "",
    related_codex_slug: (initial as { related_codex_slug?: string }).related_codex_slug ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState<"draft" | "art" | null>(null);
  const [aiHint, setAiHint] = useState("");

  const draftFn = useServerFn(generateActivationDraft);
  const artFn = useServerFn(generateActivationArt);

  // Editorial relationship options: real published records only.
  const relationsFn = useServerFn(getRelationOptions);
  const [relationOptions, setRelationOptions] = useState<RelationOptions | null>(null);
  const [relationsLoading, setRelationsLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    relationsFn({})
      .then((o) => { if (alive) setRelationOptions(o as RelationOptions); })
      .catch(() => { /* picker degrades to free text validated server-side */ })
      .finally(() => { if (alive) setRelationsLoading(false); });
    return () => { alive = false; };
  }, [relationsFn]);


  function set<K extends keyof typeof f>(k: K, v: (typeof f)[K]) { setF((prev) => ({ ...prev, [k]: v })); }

  async function aiDraft() {
    setAiBusy("draft"); setError(null);
    try {
      const d = (await draftFn({ data: { series: f.series, slot: f.slot, hint: aiHint || undefined } })) as Record<string, string>;
      setF((p) => ({
        ...p,
        title: d.title ?? p.title,
        slug: p.slug || d.slug || "",
        subtitle: d.subtitle ?? p.subtitle,
        intro: d.intro ?? p.intro,
        transmission: d.transmission ?? p.transmission,
        scroll_card: d.scroll_card ?? p.scroll_card,
        caption: d.caption ?? p.caption,
        phrase: d.phrase ?? p.phrase,
        journal: d.journal_prompt ?? p.journal,
        reflection_question: d.reflection_question ?? p.reflection_question,
        instruction: d.instruction ?? p.instruction,
        breath_pattern: d.breath_pattern ?? p.breath_pattern,
        duration: d.duration ?? p.duration,
      }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAiBusy(null);
    }
  }

  async function aiArt() {
    if (!f.title.trim()) { setError("Add a title first so the art has a subject."); return; }
    setAiBusy("art"); setError(null);
    try {
      const r = await artFn({ data: { title: f.title, series: f.series, extra: aiHint || undefined } });
      set("hero_image_url", r.url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAiBusy(null);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);

    const publish_at = f.slot === "anytime" && f.status !== "scheduled"
      ? (initial.publish_at ?? new Date().toISOString())
      : publishAtForSlot(f.publish_date, f.slot);

    const payload: Partial<Ins> = {
      slug: f.slug.trim(),
      title: f.title.trim(),
      subtitle: f.subtitle.trim() || null,
      series: f.series,
      slot: f.slot,
      status: f.status,
      publish_at,
      // legacy fields still required NOT NULL
      category: SLOT_BY_SLUG[f.slot].label,
      time_label: SLOT_BY_SLUG[f.slot].label,
      duration: f.duration.trim() || null,
      breath_pattern: f.breath_pattern || "box",
      intro: f.intro.trim() || null,
      intention: f.intention,
      instruction: f.instruction,
      transmission: f.transmission.trim() || null,
      scroll_card: f.scroll_card.trim() || null,
      caption: f.caption.trim() || null,
      phrase: f.phrase,
      journal: f.journal,
      reflection_question: f.reflection_question.trim() || null,
      hero_image_url: f.hero_image_url || null,
      wallpaper_url: f.wallpaper_url || null,
      audio_url: f.audio_url || null,
      video_url: f.video_url || null,
      // Unified publishing
      ...( {
        theme: f.theme.trim() || null,
        difficulty: f.difficulty.trim() || null,
        estimated_minutes: f.estimated_minutes ? parseInt(f.estimated_minutes, 10) : null,
        tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
        journal_prompt: f.journal_prompt.trim() || null,
        meditation: f.meditation.trim() || null,
        narration_url: f.narration_url.trim() || null,
        related_gate_n: f.related_gate_n ? parseInt(f.related_gate_n, 10) : null,
        related_music_slug: f.related_music_slug.trim() || null,
        related_book_slug: f.related_book_slug.trim() || null,
        related_wisdom_slug: f.related_wisdom_slug.trim() || null,
        related_challenge_slug: f.related_challenge_slug.trim() || null,
        related_codex_slug: f.related_codex_slug.trim() || null,
      } as Partial<Ins>),
    };

    const q = isNew
      ? supabase.from("activations").insert(payload as Ins)
      : supabase.from("activations").update(payload).eq("id", initial.id!);
    const { error } = await q;
    setBusy(false);
    if (error) setError(error.message); else onSaved();
  }

  return (
    <AdminDialog open onClose={onClose} title={isNew ? "New activation" : `Edit · ${initial.title}`}>
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-md border hairline bg-gold/[0.03] p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold">AI Draft Studio</div>
              <div className="text-xs text-muted-foreground mt-1">
                Generate a full activation draft or hero art in the current Series + Slot. Review before saving.
              </div>
            </div>
          </div>
          <Input
            value={aiHint}
            onChange={(e) => setAiHint(e.target.value)}
            placeholder="Optional direction (theme, energy, edge)…"
          />
          <div className="flex flex-wrap gap-2">
            <AdminButton type="button" onClick={aiDraft} disabled={aiBusy !== null}>
              {aiBusy === "draft" ? "Drafting…" : "✦ Draft with AI"}
            </AdminButton>
            <AdminButton type="button" variant="ghost" onClick={aiArt} disabled={aiBusy !== null}>
              {aiBusy === "art" ? "Painting…" : "✦ Generate hero art"}
            </AdminButton>
          </div>
        </div>

        <Row>
          <Field label="Title">
            <Input required value={f.title} onChange={(e) => set("title", e.target.value)} />
          </Field>
          <Field label="Slug" hint="Used in the URL. Lowercase, dashes.">
            <Input required value={f.slug} onChange={(e) => set("slug", e.target.value)} />
          </Field>
        </Row>

        <Field label="Subtitle">
          <Input value={f.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
        </Field>

        <Row>
          <Field label="Series">
            <select
              value={f.series}
              onChange={(e) => set("series", e.target.value as ActivationSeries)}
              className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold"
            >
              {ACTIVATION_SERIES.map((s) => (
                <option key={s.slug} value={s.slug}>{s.number}. {s.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Slot" hint="Sets the auto-publish hour.">
            <select
              value={f.slot}
              onChange={(e) => set("slot", e.target.value as ActivationSlot)}
              className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold"
            >
              {ACTIVATION_SLOTS.map((s) => (
                <option key={s.slug} value={s.slug}>{s.label}</option>
              ))}
            </select>
          </Field>
        </Row>

        <Row>
          <Field label="Status">
            <select
              value={f.status}
              onChange={(e) => set("status", e.target.value as ActivationStatus)}
              className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold"
            >
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published (immediate)</option>
            </select>
          </Field>
          <Field label="Publish date" hint="Combined with slot to set the exact go-live moment.">
            <Input type="date" value={f.publish_date} onChange={(e) => set("publish_date", e.target.value)} />
          </Field>
        </Row>

        <Field label="Hero image" hint="Cinematic activation poster.">
          <MediaUpload value={f.hero_image_url} onChange={(url) => set("hero_image_url", url)} folder="activations/hero" />
        </Field>

        <Field label="Intro">
          <Textarea rows={2} value={f.intro} onChange={(e) => set("intro", e.target.value)} placeholder="Official KAfrequency intro line." />
        </Field>

        <Field label="Intention" hint="One-line felt purpose, used in cards and OG description.">
          <Textarea rows={2} value={f.intention} onChange={(e) => set("intention", e.target.value)} />
        </Field>

        <Field label="Transmission" hint="The full activation body — the teaching.">
          <Textarea rows={6} value={f.transmission} onChange={(e) => set("transmission", e.target.value)} />
        </Field>

        <Field label="Scroll card" hint="Short quotable middle-of-page card.">
          <Textarea rows={2} value={f.scroll_card} onChange={(e) => set("scroll_card", e.target.value)} />
        </Field>

        <Field label="Caption" hint="Instagram/social caption.">
          <Textarea rows={3} value={f.caption} onChange={(e) => set("caption", e.target.value)} />
        </Field>

        <Row>
          <Field label="Phrase">
            <Textarea rows={2} value={f.phrase} onChange={(e) => set("phrase", e.target.value)} />
          </Field>
          <Field label="Journal prompt">
            <Textarea rows={2} value={f.journal} onChange={(e) => set("journal", e.target.value)} />
          </Field>
        </Row>

        <Field label="Reflection question">
          <Textarea rows={2} value={f.reflection_question} onChange={(e) => set("reflection_question", e.target.value)} />
        </Field>

        <Row>
          <Field label="Breath pattern" hint="box · coherent · 478">
            <Input value={f.breath_pattern} onChange={(e) => set("breath_pattern", e.target.value)} />
          </Field>
          <Field label="Duration">
            <Input value={f.duration} onChange={(e) => set("duration", e.target.value)} placeholder="7 minutes" />
          </Field>
        </Row>

        <Field label="Instruction" hint="Practical how-to (posture, breath, etc.).">
          <Textarea rows={3} value={f.instruction} onChange={(e) => set("instruction", e.target.value)} />
        </Field>

        <Field label="Wallpaper (phone/desktop)">
          <MediaUpload value={f.wallpaper_url} onChange={(url) => set("wallpaper_url", url)} folder="activations/wallpaper" />
        </Field>

        <Row>
          <Field label="Audio URL (background)">
            <Input value={f.audio_url} onChange={(e) => set("audio_url", e.target.value)} placeholder="https://…" />
          </Field>
          <Field label="Video URL">
            <Input value={f.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://…" />
          </Field>
        </Row>

        <div className="rounded-md border hairline bg-obsidian/40 p-4 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold">Ecosystem metadata</div>
          <Row>
            <Field label="Theme" hint="One-word or phrase, e.g. ‘Discipline’.">
              <Input value={f.theme} onChange={(e) => set("theme", e.target.value)} />
            </Field>
            <Field label="Difficulty">
              <select
                value={f.difficulty}
                onChange={(e) => set("difficulty", e.target.value)}
                className="w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold"
              >
                <option value="">—</option>
                <option value="gentle">Gentle</option>
                <option value="steady">Steady</option>
                <option value="deep">Deep</option>
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Estimated minutes">
              <Input type="number" min={1} max={120} value={f.estimated_minutes} onChange={(e) => set("estimated_minutes", e.target.value)} />
            </Field>
            <Field label="Tags" hint="Comma-separated (e.g. discipline, gate-3, breath).">
              <Input value={f.tags} onChange={(e) => set("tags", e.target.value)} />
            </Field>
          </Row>
          <Field label="Narration audio URL" hint="Optional voice-guided narration (distinct from background audio).">
            <Input value={f.narration_url} onChange={(e) => set("narration_url", e.target.value)} placeholder="https://…" />
          </Field>
          <Row>
            <Field label="Journal prompt (short)" hint="Short prompt shown in cards and notifications.">
              <Textarea rows={2} value={f.journal_prompt} onChange={(e) => set("journal_prompt", e.target.value)} />
            </Field>
            <Field label="Meditation" hint="Optional guided meditation script or notes.">
              <Textarea rows={2} value={f.meditation} onChange={(e) => set("meditation", e.target.value)} />
            </Field>
          </Row>
        </div>

        <div className="rounded-md border hairline bg-obsidian/40 p-4 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold">Related content</div>
          <p className="text-xs text-muted-foreground">
            Only published repository records can be linked. Leave a field empty for no relationship —
            nothing is inferred automatically.
          </p>
          <Row>
            <Field label="Related Gate (#)" hint="1–47">
              <Input type="number" min={1} max={47} value={f.related_gate_n} onChange={(e) => set("related_gate_n", e.target.value)} />
            </Field>
            <SlugPicker
              label="Related music"
              hint="Published release"
              value={f.related_music_slug}
              options={relationOptions?.music ?? []}
              loading={relationsLoading}
              onChange={(v) => set("related_music_slug", v)}
            />
          </Row>
          <Row>
            <SlugPicker
              label="Related book"
              hint="Published book"
              value={f.related_book_slug}
              options={relationOptions?.books ?? []}
              loading={relationsLoading}
              onChange={(v) => set("related_book_slug", v)}
            />
            <SlugPicker
              label="Related teaching (Wisdom)"
              hint="Published wisdom post"
              value={f.related_wisdom_slug}
              options={relationOptions?.wisdom ?? []}
              loading={relationsLoading}
              onChange={(v) => set("related_wisdom_slug", v)}
            />
          </Row>

          <Row>
            <Field label="Related challenge slug">
              <Input value={f.related_challenge_slug} onChange={(e) => set("related_challenge_slug", e.target.value)} />
            </Field>
            <Field label="Related Codex concept slug">
              <Input value={f.related_codex_slug} onChange={(e) => set("related_codex_slug", e.target.value)} />
            </Field>
          </Row>
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 border-t hairline pt-4">
          {!isNew && initial.slug && (
            <a
              href={`/activations/${initial.slug}`}
              target="_blank"
              rel="noreferrer"
              className="rounded-full border hairline px-4 py-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:border-gold hover:text-gold"
            >
              Preview ↗
            </a>
          )}
          <AdminButton type="button" variant="ghost" onClick={onClose}>Cancel</AdminButton>
          <AdminButton type="submit" disabled={busy}>{busy ? "Saving…" : isNew ? "Create" : "Save"}</AdminButton>
        </div>
      </form>
    </AdminDialog>
  );
}
